import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { noop } from 'ui-components/default-props/noop';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import {
  ONBOARDING_CONNECTION_SUCCESS_TOAST_CLICK,
  ONBOARDING_CONNECTION_SUCCESS_TOAST_CLOSE,
} from '../../../../constants/analytics-events';

import ModalPortal from '../../../../components/modal-portal';
import { CuiMarketplaceConnection } from '../../../../connection-components/marketplace-connection';
import { useScreenSizeContext } from '../../../../contexts/screen-size-resolution';
import { useSegmentTracking } from './onboard-page-notification-segment-tracking';
import { MobileContent, DesktopContent } from './store-connection-success-notifications';
import style from './style.module.less';
import messages from '../../../onboard/steps/marketplace-step/messages';

const MarketplaceSetupModal = ({
  onClose,
  onError = noop,
  marketplaceApiCode,
  onSave,
  redirectPath = '',
  onBack = noop,
  onRedirect = noop,
  showNotification = true,
  storeGuid,
}) => {
  const { isMobile } = useScreenSizeContext();
  const trackBodyClick = useSegmentTracking(ONBOARDING_CONNECTION_SUCCESS_TOAST_CLICK);
  const trackClose = useSegmentTracking(ONBOARDING_CONNECTION_SUCCESS_TOAST_CLOSE);

  const saveAndNotify = useCallback(
    (savedStore) => {
      onSave(savedStore);
      if (showNotification) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...messages.saveSuccessTitle} />,
          content: isMobile ? MobileContent : DesktopContent,
          dontHide: true,
          onClick: trackBodyClick,
          trackNotificationClose: trackClose,
          withMobileStyling: isMobile,
        });
      }
    },
    [isMobile, onSave, showNotification, trackBodyClick, trackClose],
  );

  return (
    <ModalPortal containerId='marketplace-setup-modal' onClose={onClose}>
      <Modal className={classnames(style['marketplace-settings-modal'])}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage
            defaultMessage='Set Up Store Connection'
            description='src.pages.settings.stores.marketplace-setup-modal'
            id='kJKjAm'
          />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <CuiMarketplaceConnection
            marketplaceApiCode={marketplaceApiCode}
            onBack={onBack}
            onCancel={onClose}
            onError={onError}
            onRedirect={onRedirect}
            onSave={saveAndNotify}
            redirectPath={redirectPath}
            storeGuid={storeGuid}
          />
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};

MarketplaceSetupModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func,
  marketplaceApiCode: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  redirectPath: PropTypes.string,
  onBack: PropTypes.func,
  onRedirect: PropTypes.func,
  showNotification: PropTypes.bool,
};

export default MarketplaceSetupModal;
